import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { ConfirmDestructiveDialog } from '@/shared/components/ConfirmDestructiveDialog';
import { SettingsPanel } from '@/shared/components/SettingsPanel';
import { formatExactTime, formatRelativeTime } from '@/shared/utils/time';

import { EDITOR_COPY } from '../copy';
import { knownTool, toolLabel } from '../knownTools';
import { useDisconnectAiTools, useMyAiTools } from '../myAiTools';
import { ToolMark } from './ToolMark';

/**
 * The AI tools that have acted as this person (spec 245): each with its mark,
 * when it was last used and when it first connected, latest first.
 *
 * Nothing at all until there is one, and nothing while it loads or if the read
 * fails: the setup below is what the page is for, and a person with no tool
 * has nothing to see here.
 *
 * One Disconnect for them all (spec 247): every tool signs in through the same
 * client, so none can be cut off alone, and the button says so.
 */
export function YourAiTools() {
  const tools = useMyAiTools();
  const disconnect = useDisconnectAiTools();
  const [confirming, setConfirming] = useState(false);
  const items = tools.data?.items ?? [];
  if (items.length === 0) return null;
  const names = new Intl.ListFormat('en', { type: 'conjunction' }).format(
    items.map((item) => toolLabel(item.client_name)),
  );

  return (
    <SettingsPanel labelledBy="editor-yours">
      {/* On a phone Disconnect all leaves the heading's side, where it
          squeezed the hint, for a full-width row under the list (spec 275). */}
      <div className="flex items-start justify-between gap-3 max-md:contents">
        <div className="flex flex-col gap-1">
          <h2 id="editor-yours" className="text-foreground text-sm font-medium">
            {EDITOR_COPY.yoursHeading}
          </h2>
          <p className="text-muted-foreground text-sm">
            {EDITOR_COPY.yoursHint}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="shrink-0 max-md:order-last max-md:w-full"
          onClick={() => {
            disconnect.reset();
            setConfirming(true);
          }}
        >
          {EDITOR_COPY.disconnectAll}
        </Button>
      </div>
      <ul className="divide-border flex flex-col divide-y">
        {items.map((item) => {
          const label = toolLabel(item.client_name);
          return (
            <li
              key={item.client_name}
              className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0"
            >
              <span className="bg-muted text-foreground flex size-8 shrink-0 items-center justify-center rounded-md">
                <ToolMark
                  tool={knownTool(item.client_name) ?? 'other'}
                  className="size-4"
                />
              </span>
              <div className="flex min-w-0 flex-col">
                <span
                  className="text-foreground truncate text-sm font-medium"
                  // An unknown tool is shown by its own name; a known one says
                  // what it reported, for a person checking.
                  title={
                    label === item.client_name ? undefined : item.client_name
                  }
                >
                  {label}
                </span>
                <span className="text-muted-foreground text-xs">
                  <time
                    dateTime={item.last_used_at}
                    title={formatExactTime(item.last_used_at)}
                  >
                    {EDITOR_COPY.lastUsed(
                      formatRelativeTime(item.last_used_at),
                    )}
                  </time>
                  {' · '}
                  <time
                    dateTime={item.first_used_at}
                    title={formatExactTime(item.first_used_at)}
                  >
                    {EDITOR_COPY.firstConnected(day(item.first_used_at))}
                  </time>
                </span>
              </div>
            </li>
          );
        })}
      </ul>
      <ConfirmDestructiveDialog
        open={confirming}
        title={EDITOR_COPY.disconnectTitle}
        confirmLabel={EDITOR_COPY.disconnect}
        pendingLabel={EDITOR_COPY.disconnecting}
        isPending={disconnect.isPending}
        error={disconnect.error?.message ?? null}
        onConfirm={() => {
          disconnect.mutate(undefined, {
            onSuccess: () => {
              setConfirming(false);
              toast.success(EDITOR_COPY.disconnected);
            },
          });
        }}
        onCancel={() => setConfirming(false)}
      >
        {EDITOR_COPY.disconnectBody(names)}
      </ConfirmDestructiveDialog>
    </SettingsPanel>
  );
}

/** "12 Oct 2026": a day is all "first connected" needs. */
function day(at: string): string {
  return new Date(at).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}
